import { type HTMLAttributes, type ReactNode } from 'react';

import {
  CheckCircleIcon,
  CloseIcon,
  ErrorCircleIcon,
  InfoFilledIcon,
  InfoIcon,
  WarningFilledIcon,
} from '@tankef/icons';

import { cn } from '../../utils';

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'info' | 'info-dark' | 'success' | 'warning' | 'error';
  title?: string;
  icon?: ReactNode;
  onClose?: () => void;
  children: ReactNode;
}

// Text uses *-800 (dark) on *-50 (light) backgrounds — WCAG ratios:
//   info    primary-900 on primary-50  → ~6.5:1 ✅  (primary-800 was only 3.49:1 ❌)
//   success success-800 on success-50  → ~6.3:1 ✅
//   warning warning-800 on warning-50  → ~6.3:1 ✅
//   error   error-800   on error-50    → ~10.8:1 ✅
// Icons use *-700 (not *-500) on *-50 backgrounds — *-500 on *-50 gives 2:1 ❌
const variantStyles = {
  info: {
    container: 'bg-primary-50 border-primary-200 text-primary-900',
    icon: 'text-primary-700',
  },
  success: {
    container: 'bg-success-50 border-success-200 text-success-800',
    icon: 'text-success-700',
  },
  warning: {
    container: 'bg-warning-50 border-warning-200 text-warning-800',
    icon: 'text-warning-700',
  },
  error: {
    container: 'bg-error-50 border-error-200 text-error-800',
    icon: 'text-error-700',
  },
  'info-dark': {
    container: 'bg-alert-info-dark-bg border-transparent text-white',
    icon: 'text-white',
  },
};

const defaultIcons: Record<string, ReactNode> = {
  info: <InfoFilledIcon />,
  success: <CheckCircleIcon />,
  warning: <WarningFilledIcon />,
  error: <ErrorCircleIcon />,
  'info-dark': <InfoIcon />,
};

export function Alert({
  variant = 'info',
  title,
  icon,
  onClose,
  className,
  children,
  ...props
}: AlertProps) {
  const styles = variantStyles[variant];
  const displayIcon = icon ?? defaultIcons[variant];

  return (
    <div
      className={cn('flex rounded-lg border px-4 py-3', styles.container, className)}
      role="alert"
      {...props}
    >
      {displayIcon && <div className={cn('flex-shrink-0', styles.icon)}>{displayIcon}</div>}
      <div className={cn('flex-1', displayIcon && 'ml-3')}>
        {title && <h3 className="font-medium">{title}</h3>}
        <div className={cn('text-sm', title && 'mt-1')}>{children}</div>
      </div>
      {onClose && (
        <button
          type="button"
          className={cn(
            'ml-4 inline-flex flex-shrink-0 rounded-md p-1.5',
            'focus:outline-none focus:ring-2 focus:ring-offset-2',
            variant !== 'info-dark' && 'hover:bg-tkf-overlay-light',
            variant === 'info' && 'focus:ring-primary-500',
            variant === 'info-dark' && 'hover:bg-white/10 focus:ring-primary-500',
            variant === 'success' && 'focus:ring-success-500',
            variant === 'warning' && 'focus:ring-warning-500',
            variant === 'error' && 'focus:ring-tkf-line-error'
          )}
          onClick={onClose}
          aria-label="Cerrar"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
